import vm from "node:vm";
import { AutomateTask, success } from "@iflyrpa/share";
import { DomUtils, parseDocument } from "htmlparser2";
import { Http } from "../../utils/http";
import type { FetchArticlesParams } from ".";
import { errorResponse, type FetchArticlesResponse, type WeixinArticle } from "./types";

interface PublishInfo {
    type: number;
    msgid: number;
    appmsg_info: Array<{
        share_type: number;
        appmsgid: number;
        content_url: string;
        title: string;
        is_deleted: boolean;
        copyright_status: number;
        copyright_type: number;
        read_num: number;
        like_num: number;
        comment_id: string;
        is_comment_enable: boolean;
        comment_num: number;
        cover: string;
        smart_product: number;
        modify_status: number;
        appmsg_like_type: number;
        can_delete_status: number;
        old_like_num: number;
        itemidx: number;
        is_pay_subscribe: number;
        is_from_transfer: number;
        public_tag_info: {
            modify_times: number;
            init_tag_list_size: number;
        };
        digest: string;
        is_cooling_article: number;
        pic_cdn_url_235_1: string;
        pic_cdn_url_16_9: string;
        pic_cdn_url_3_4: string;
        pic_cdn_url_1_1: string;
        disable_recommend: number;
        line_info: {
            use_line: number;
            line_count: number;
            send_time: number;
            is_appmsg_flag: number;
            is_use_flag: number;
        };
        claim_source_type: number;
        share_num: number;
        is_rumor_refutation: number;
        segment_comment_id: string;
        is_segment_comment_enable: boolean;
        multi_picture_cover: number;
        moment_like_num: number;
        item_show_type: number;
    }>;
    copy_appmsg_id: number;
    new_publish: number;
}

interface BasePublishItem<T = string> {
    publish_type: number;
    publish_info: T;
}

interface BaseParsedPublishPage<T = string> {
    total_count: number;
    publish_count: number;
    masssend_count: number;
    publish_list: BasePublishItem<T>[];
    featured_count: number;
}

type ParsedPublishPage = BaseParsedPublishPage<PublishInfo>;

export async function handleWeixinData(
    _task: AutomateTask,
    params: FetchArticlesParams,
): Promise<FetchArticlesResponse> {
    const {
        cookies,
        token,
        pageNum = 1,
        pageSize = 10,
        showOriginalData = false,
        onlySuccess = false,
        cursor,
        lastPage,
        containsArticle = false,
    } = params;
    if (!token) return { code: 414, message: "缺少token", data: {} };
    const http = new Http({
        headers: {
            cookie: cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        },
    });
    const ParsePublishPage = (html: string): ParsedPublishPage => {
        const document = parseDocument(html);
        // console.log(document);

        const scripts = DomUtils.findAll((el) => el.type === "script", document.children);
        // console.log(scripts);

        // 3. 准备 vm 沙箱
        const sandbox: any = {
            window: {},
        };

        vm.createContext(sandbox);

        // 4. 执行包含 wx.cgiData 的脚本
        for (const script of scripts) {
            const code = DomUtils.textContent(script);

            if (!code) continue;

            if (code.includes("publish_page")) {
                const replaceCode = code.replace("publish_page =", "window.publish_page =");
                try {
                    vm.runInContext(replaceCode, sandbox, {
                        timeout: 1000,
                    });
                } catch (e) {
                    // 非关键脚本可忽略
                }
            }
        }
        const decodeHtmlEntities = (encodedStr: string): string => {
            return encodedStr
                .replace(/&quot;/g, '"')
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&#39;/g, "'");
        };

        sandbox.window.publish_page.publish_list.forEach((it: { publish_info: string }) => {
            it.publish_info = JSON.parse(decodeHtmlEntities(it.publish_info));
        });

        return sandbox.window.publish_page;
    };
    async function fetchArticles(begin: number, pageSize: number, token: string | number): Promise<string> {
        return http.api<string>({
            method: "get",
            url: "https://mp.weixin.qq.com/cgi-bin/appmsgpublish",
            params: {
                sub: "list",
                begin: begin,
                count: pageSize,
                token: token,
                lang: "zh_CN",
            },
        });
    }

    const size = onlySuccess ? 10 : pageSize > 20 ? 20 : pageSize;
    let begin = (lastPage ?? pageNum) - 1; // 转换为 0-based index
    const rawArticlesInfo = await fetchArticles(begin * size, size, token);
    if (rawArticlesInfo.includes("请重新<a id='jumpUrl' href='/'>登录</a>"))
        return errorResponse("微信公众号数据获取失败，请检查账号状态", 414);
    const articlesInfo = ParsePublishPage(rawArticlesInfo);
    let articleCell: WeixinArticle[] = [];

    if (!onlySuccess && pageSize > 20) {
        let totalFetched = articlesInfo.publish_list.length;
        let nextPage = pageNum + 1;

        while (totalFetched < pageSize) {
            const remaining = pageSize - totalFetched;
            const currentPageSize = remaining > 20 ? 20 : remaining;

            const _rawArticlesInfo = await fetchArticles((nextPage - 1) * 20, currentPageSize, token);
            const parsed = ParsePublishPage(_rawArticlesInfo);

            articlesInfo.publish_list = articlesInfo.publish_list.concat(parsed.publish_list);
            totalFetched = articlesInfo.publish_list.length;

            if (parsed.publish_list.length < currentPageSize) break;
            nextPage++;
        }
    }

    let nextPage = false;
    if (onlySuccess) {
        let filteredList = articlesInfo.publish_list
            .filter((item) => item.publish_info.appmsg_info[0].content_url.indexOf("tempkey") === -1)
            .map((item) => item.publish_info);

        if (cursor) {
            const cursorIndex = filteredList.findIndex((item) => item.msgid === cursor);
            if (cursorIndex !== -1) filteredList = filteredList.slice(cursorIndex + 1);
        }

        let totalFetched = filteredList.length;

        while (totalFetched < pageSize && begin * size < articlesInfo.total_count) {
            begin += 1; // 页数递增
            const nextRawArticlesInfo = await fetchArticles(begin * size, size, token);
            const nextArticlesInfo = ParsePublishPage(nextRawArticlesInfo);
            const newFiltered = nextArticlesInfo.publish_list
                .filter((item) => item.publish_info.appmsg_info[0].content_url.indexOf("tempkey") === -1)
                .map((item) => item.publish_info);
            filteredList = filteredList.concat(newFiltered);
            totalFetched = filteredList.length;
        }

        filteredList = filteredList.slice(0, pageSize);
        totalFetched = filteredList.length;

        if (totalFetched >= pageSize && begin * size < articlesInfo.total_count) {
            const nextRawArticlesInfo = await fetchArticles((begin + 1) * size, size, token);
            const nextArticlesInfo = ParsePublishPage(nextRawArticlesInfo);
            nextPage =
                nextArticlesInfo.publish_list.length > 0 &&
                nextArticlesInfo.publish_list.some(
                    (item) => item.publish_info.appmsg_info[0].content_url.indexOf("tempkey") === -1,
                );
        }

        const getDetailContent = async (id: number) => {
            try {
                const detailInfo = await http.api<string>({
                    method: "get",
                    url: "https://mp.weixin.qq.com/cgi-bin/appmsg",
                    params: {
                        token,
                        t: "media/appmsg_edit",
                        action: "edit",
                        type: 77,
                        appmsgid: id,
                        isMul: 1,
                        get_delete: 1,
                        re_edit: 1,
                        lang: "zh_CN",
                        timestamp: Date.now(),
                    },
                });

                const text = detailInfo.match(/var\s+infos\s*=\s*({[\s\S]*?})\s*,\s*item\s*=/);
                if (!text) {
                    const isCheckPass = detailInfo.indexOf("环境异常");
                    if (isCheckPass > -1) return "当前环境异常，无法获取文章内容";
                    return null;
                }

                const deatil: { item: { content: string }[] } = JSON.parse(text[1].trim());

                return deatil.item[0].content;
            } catch (err) {
                console.error("❌ 解析失败:", err);
                return null;
            }
        };

        const lastMsgId = filteredList.length > 0 ? filteredList[filteredList.length - 1].msgid : undefined;
        articleCell = await Promise.all(
            filteredList.map(async (item) => ({
                title: item.appmsg_info[0].title,
                imageUrl: item.appmsg_info[0].cover,
                createTime: item.appmsg_info[0].line_info.send_time,
                redirectUrl: item.appmsg_info[0].content_url,
                readNum: item.appmsg_info[0].read_num,
                likeNum: item.appmsg_info[0].old_like_num,
                shareNum: item.appmsg_info[0].share_num,
                recommendNum: item.appmsg_info[0].like_num,
                ...(showOriginalData ? { originalData: item } : {}),
                ...(containsArticle ? { article: await getDetailContent(item.msgid) } : {}),
            })),
        );

        return success(
            {
                articleCell,
                pagination: {
                    nextPage,
                    pageNum,
                    pageSize,
                    lastPage: begin + 1, // 返回当前页数
                    cursor: lastMsgId, // 返回最后一条的 msgid
                },
            },
            "微信文章文章获取成功",
        );
    }

    const BasePublishBUilder = (publishInfo: BasePublishItem<PublishInfo>[]): WeixinArticle[] => {
        return publishInfo.map((item) => ({
            title: item.publish_info.appmsg_info[0].title,
            imageUrl: item.publish_info.appmsg_info[0].cover,
            createTime: item.publish_info.appmsg_info[0].line_info.send_time,
            redirectUrl: item.publish_info.appmsg_info[0].content_url,
            readNum: item.publish_info.appmsg_info[0].read_num,
            likeNum: item.publish_info.appmsg_info[0].old_like_num,
            shareNum: item.publish_info.appmsg_info[0].share_num,
            recommendNum: item.publish_info.appmsg_info[0].like_num,
            ...(showOriginalData ? { originalData: item } : {}),
        }));
    };

    articleCell = BasePublishBUilder(articlesInfo.publish_list);

    const timerPublishData = await http.api({
        url: "https://mp.weixin.qq.com/cgi-bin/home",
        method: "get",
        params: {
            t: "home/index",
            token,
            lang: "zh_CN",
        },
    });

    const decodeTimerPublishData = (html: string): BasePublishItem<PublishInfo>[] | [] => {
        const document = parseDocument(html);
        // console.log(document);

        const scripts = DomUtils.findAll((el) => el.type === "script", document.children);

        // 3. 准备 vm 沙箱
        const sandbox: any = {
            window: {},
        };

        vm.createContext(sandbox);
        // 4. 执行包含 wx.cgiData 的脚本
        for (const script of scripts) {
            let code = DomUtils.textContent(script);

            if (!code) continue;

            if (code.includes("wx.cgiData")) {
                code = code.replace("wx.cgiData", "window.wx.cgiData");
                try {
                    vm.runInContext(code, sandbox, {
                        timeout: 1000,
                    });
                } catch (e) {
                    // 非关键脚本可忽略
                }
            }
        }
        if (!sandbox.window.wx || !sandbox.window.wx.cgiData || !sandbox.window.wx.cgiData.timesend_msg) {
            return [];
        }
        const timesend_msg = JSON.parse(sandbox.window.wx.cgiData.timesend_msg);
        return timesend_msg.sent_list.map((it: PublishInfo) => {
            return {
                publish_info: it,
            };
        });
    };

    const timmerPublish = decodeTimerPublishData(timerPublishData);
    return success(
        {
            ...(timmerPublish.length === 0 ? {} : { timmerPublish: BasePublishBUilder(timmerPublish) }),
            articleCell,
        },
        "微信文章文章获取成功",
    );
}
